import {
  AutomationsPresentationV1Edge,
  AutomationsPresentationV1Node
} from '../../types'
import { collectReachableNodeIds } from '../collectReachableNodeIds'
import { ReminderRecipients } from './findClosestUpstreamReminderRecipients'
import { prefillReminderRecipientsFromUpstream } from './prefillReminderRecipientsFromUpstream'

/**
 * Fill recipient addresses (to/cc/bcc) on the reminders affected by a
 * newly-added connection (BADGE-5974). Runs only when `edges` contains an edge
 * not present in `previousEdges` — i.e. a connection was just made (by dragging
 * a connection, dropping in a node, or inserting one on an edge) — and only for
 * reminder nodes downstream of those new edges. So the newly-connected reminder
 * and any already-connected-but-default reminders after it inherit the closest
 * upstream reminder's addresses, while config edits, drags and initial load are
 * left untouched.
 *
 * Returns the original `nodes` array reference when nothing changed.
 */
export const applyReminderRecipientDefaults = ({
  nodes,
  edges,
  previousEdges,
  defaultRecipients
}: {
  nodes: AutomationsPresentationV1Node[]
  edges: AutomationsPresentationV1Edge[]
  previousEdges: AutomationsPresentationV1Edge[] | undefined
  defaultRecipients: ReminderRecipients
}): AutomationsPresentationV1Node[] => {
  // No baseline (initial load) means no "new" connection to react to.
  if (!previousEdges) return nodes

  const previousEdgeIds = new Set(previousEdges.map(edge => edge.id))
  const newEdges = edges.filter(edge => !previousEdgeIds.has(edge.id))
  if (newEdges.length === 0) return nodes

  const downstreamIds = new Set(
    newEdges.flatMap(edge =>
      Array.from(
        collectReachableNodeIds({
          startId: edge.target,
          edges,
          direction: 'downstream'
        })
      )
    )
  )

  return Array.from(downstreamIds).reduce(
    (accNodes, nodeId) =>
      prefillReminderRecipientsFromUpstream({
        nodeId,
        nodes: accNodes,
        edges,
        defaultRecipients
      }),
    nodes
  )
}
